import { Component, OnInit, OnDestroy, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { Subject, takeUntil } from "rxjs";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { environment } from "../../../../../environments/environment";
import { SessionService } from "../../../../core/services/session.service";
import { QPilotAccessToken } from "../../../../core/services/qpilot.service";
import { MenuItem } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { DashboardNavigationService } from "../../../../core/services/dashboard-navigation.service";

@Component({
  selector: "app-legacy-dashboard",
  standalone: true,
  imports: [CommonModule, BreadcrumbModule],
  template: `
    <div class="iframe-container">
      <iframe
        *ngIf="dashboardUrl"
        [src]="dashboardUrl"
        class="iframe-content"
        frameborder="0"
      ></iframe>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        flex: 1;
        overflow: hidden;
        background-color: white;
      }

      .iframe-container {
        width: 100%;
        flex: 1;
        display: flex;
        overflow: hidden;
        min-height: 0;
      }

      .iframe-content {
        width: 100%;
        height: 100%;
        border: none;
        flex: 1;
      }
    `,
  ],
})
export class LegacyDashboardComponent implements OnInit, OnDestroy {
  // Subject for cleanup on destroy
  private readonly destroy$ = new Subject<void>();

  // Input properties for embedded mode
  @Input() inputSiteId?: string | null;
  @Input() inputToken?: QPilotAccessToken | null;
  @Input() embeddedMode: boolean = false;

  dashboardUrl: SafeResourceUrl | null = null;
  breadcrumbItems: MenuItem[] = [{ label: "Sites", routerLink: "/sites" }];
  home: MenuItem = { icon: "pi pi-home", routerLink: "/sites" };
  siteId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private sessionService: SessionService,
    private dashboardNavigationService: DashboardNavigationService,
  ) {}

  ngOnInit(): void {
    // Check if in embedded mode with inputs provided
    if (this.embeddedMode && this.inputSiteId) {
      this.initializeDashboard(this.inputSiteId);
    } else {
      // Standard mode: get siteId from route params
      this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        const siteId = params.get("siteId");
        if (siteId) {
          this.breadcrumbItems.push({
            label: "Dashboard",
            routerLink: this.dashboardNavigationService.getDashboardRoute(
              "",
              siteId,
            ),
          });
          this.initializeDashboard(siteId);
        }
      });
    }
  }

  private initializeDashboard(siteId: string): void {
    // Sanitize and validate the siteId
    const sanitizedSiteId = this.sanitizeSiteId(siteId);
    if (sanitizedSiteId) {
      // Get the token: use inputToken in embedded mode, sessionService in standard mode
      const tokenBearerAuth =
        this.embeddedMode && this.inputToken
          ? this.inputToken.TokenBearerAuth
          : this.sessionService.accessToken?.TokenBearerAuth;

      // Create the URL for the legacy dashboard
      const baseUrl = environment.qpilotLegacyQMCUrl;
      const dashboardUrl = `${baseUrl}/widgets/dashboard/${sanitizedSiteId}/dashboard?tokenBearerAuth=${encodeURIComponent(tokenBearerAuth || "")}`;

      this.dashboardUrl =
        this.sanitizer.bypassSecurityTrustResourceUrl(dashboardUrl);
    }
  }

  private sanitizeSiteId(siteId: string): string | null {
    // Basic validation - ensure it only contains digits
    if (/^\d+$/.test(siteId)) {
      return siteId;
    }

    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
